// ============================================================================
// UNIFIED LOG MANAGEMENT TYPES
// ============================================================================

/**
 * Enumeration of all supported log types in the comprehensive log management system
 */
export enum LogType {
  ERROR_LOGS = 'error-logs',
  USER_ACTION_LOGS = 'user-action-logs',
  API_LOGS = 'api-logs',
  DATABASE_LOGS = 'database-logs',
  SECURITY_LOGS = 'security-logs'
}

/** Progress events while chunk-purging log tables and reclaiming disk space. */
export type LogPurgePhase = 'counting' | 'deleting' | 'reclaiming' | 'done';

export interface LogPurgeProgress {
  phase: LogPurgePhase;
  deletedCount: number;
  totalCount: number;
}

/**
 * Log levels used across all log types
 */
export type LogLevel = 'error' | 'warning' | 'info' | 'debug';

/**
 * Security event types for security logs
 */
export type SecurityEventType =
  | 'authentication_attempt'
  | 'authorization_failure'
  | 'suspicious_activity'
  | 'data_access'
  | 'configuration_change'
  | 'security_violation'
  | 'threat_detected';

/**
 * Security severity levels
 */
export type SecuritySeverity = 'low' | 'medium' | 'high' | 'critical';

// ============================================================================
// BASE LOG INTERFACE
// ============================================================================

/**
 * Base interface for all log types containing common fields
 */
export interface BaseLogDto {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  source: string;
  category?: string;
  userId?: string;
  ipAddress?: string;
  traceId?: string;
  tags?: string[];
  metadata?: Record<string, any>;
}

// ============================================================================
// SPECIALIZED LOG INTERFACES
// ============================================================================

/**
 * Raw error log response from API (matches actual API structure)
 */
export interface RawErrorLogDto {
  level: string;
  source: string;
  message: string;
  details?: string;
  exceptionType?: string;
  stackTrace?: string;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
  requestPath?: string;
  httpMethod?: string;
  traceId?: string;
  timestamp?: string;
  // Generate a unique ID for frontend use
  id?: string;
  resolved?: boolean;
}

/**
 * Error log interface extending base log with error-specific fields
 */
export interface ErrorLogDto extends BaseLogDto {
  stackTrace?: string;
  endpoint?: string;
  resolved?: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  // Additional fields from actual API response
  details?: string;
  exceptionType?: string;
  userAgent?: string;
  requestPath?: string;
  httpMethod?: string;
}

/**
 * System log interface for general system events
 */
export interface SystemLogDto extends BaseLogDto {
  eventType?: string;
  systemComponent?: string;
  performanceMetrics?: Record<string, number>;
}

/**
 * User action log interface for tracking user activities
 */
export interface UserActionLogDto extends BaseLogDto {
  action: string;
  resourceType: string;
  resourceId?: string;
  sessionId?: string;
  userAgent?: string;
  previousValue?: any;
  newValue?: any;
  duration?: number;
  // Additional fields from backend
  userEmail?: string;
  category?: string;
  resource?: string;
  description?: string;
  oldValues?: any;
  newValues?: any;
  additionalData?: any;
  requestPath?: string;
  httpMethod?: string;
  responseTimeMs?: number;
  statusCode?: number;
  isSuccess?: boolean;
  errorMessage?: string;
  createdAt?: string; // Backend uses this instead of timestamp
  // Alternative field names that might be used by backend
  oldValue?: any;
  before?: any;
  originalValue?: any;
  after?: any;
  updatedValue?: any;
  currentValue?: any;
  error?: string;
  exception?: string;
  failureReason?: string;
}

/**
 * DTO for creating user action logs - matches swagger.json CreateUserActionLogDto schema exactly
 */
export interface CreateUserActionLogDto {
  // Required fields (must be present)
  userId: string; // maxLength: 50
  action: string; // maxLength: 100
  category: string; // maxLength: 100
  resource: string; // maxLength: 200
  description: string; // minLength: 1
  
  // Optional fields
  resourceId?: string | null;
  oldValues?: string | null; // JSON string
  newValues?: string | null; // JSON string
  additionalData?: string | null; // JSON string
  ipAddress?: string | null;
  userAgent?: string | null;
  requestPath?: string | null;
  httpMethod?: string | null;
  traceId?: string | null;
  responseTimeMs?: number | null;
  statusCode?: number | null;
  isSuccess?: boolean;
  errorMessage?: string | null;
}

/**
 * DTO for creating database logs - matches swagger.json CreateDatabaseLogDto schema exactly
 */
export interface CreateDatabaseLogDto {
  // Required fields (must be present)
  operation: string; // maxLength: 50
  tableName: string; // maxLength: 100
  
  // Optional fields
  entityId?: string | null;
  userId?: string | null;
  oldValues?: string | null; // JSON string
  newValues?: string | null; // JSON string
  query?: string | null;
  executionTimeMs?: number; // int64
  rowsAffected?: number | null; // int32
  isSuccess?: boolean;
  errorMessage?: string | null;
  traceId?: string | null;
}

/**
 * API log interface for HTTP request/response audit entries
 */
export interface ApiLogDto extends BaseLogDto {
  logId?: string;
  httpMethod: string;
  endpoint: string;
  statusCode: number;
  responseTimeMs: number;
  requestSizeBytes?: number;
  responseSizeBytes?: number;
  userEmail?: string;
  userName?: string;
  userAgent?: string;
  correlationId?: string;
  isSuccess?: boolean;
  errorMessage?: string;
  createdAt?: string;
  method?: string;
  responseTime?: number;
}

export interface CreateApiLogDto {
  httpMethod: string;
  endpoint: string;
  statusCode: number;
  responseTimeMs: number;
  requestSizeBytes?: number;
  responseSizeBytes?: number;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
  traceId?: string;
  correlationId?: string;
  source?: string;
  category?: string;
  message?: string;
  isSuccess?: boolean;
  errorMessage?: string;
}

/**
 * Database log interface for database operations
 */
export interface DatabaseLogDto extends BaseLogDto {
  queryType: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'DDL' | 'TRANSACTION';
  executionTime: number;
  rowsAffected?: number;
  queryHash?: string;
  connectionId?: string;
  databaseName?: string;
  tableName?: string;
  queryText?: string;
}

/**
 * Security log interface for security events
 */
export interface SecurityLogDto extends BaseLogDto {
  eventType: SecurityEventType;
  severity: SecuritySeverity;
  threatLevel: number;
  sourceIp: string;
  targetResource?: string;
  authenticationMethod?: string;
  riskScore?: number;
  mitigationActions?: string[];
  alertTriggered?: boolean;
}

// ============================================================================
// FILTERING AND QUERY INTERFACES
// ============================================================================

/**
 * Base query parameters for log retrieval
 */
export interface LogQueryParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  level?: LogLevel | LogLevel[] | string;
  source?: string | string[];
  category?: string | string[];
  userId?: string | string[];
  ipAddress?: string | string[];
  traceId?: string;
  startDate?: string;
  endDate?: string;
  searchTerm?: string;
  tags?: string[];
}

/**
 * Enhanced query parameters with additional filtering options
 */
export interface EnhancedLogQueryParams extends LogQueryParams {
  resolved?: boolean; // For error logs
  eventType?: SecurityEventType | SecurityEventType[]; // For security logs
  severity?: SecuritySeverity | SecuritySeverity[]; // For security logs
  method?: string | string[]; // For API logs and user action logs
  endpoint?: string | string[]; // For API logs
  statusCode?: number | number[]; // For API logs
  queryType?: string | string[]; // For database logs
  action?: string | string[]; // For user action logs
  resourceType?: string | string[]; // For user action logs
  minExecutionTime?: number; // For database logs
  maxExecutionTime?: number; // For database logs
  minResponseTime?: number; // For API logs
  maxResponseTime?: number; // For API logs
  // New user action log parameters based on swagger.json
  userName?: string; // Search by user email or username (UserName in API)
  resource?: string; // Filter by resource name (Resource in API)
  isResolved?: boolean; // Filter by resolution status (IsResolved in API)
  sortDirection?: 'asc' | 'desc'; // Sort direction (SortDirection in API)
  logId?: number; // Filter by specific log ID (LogId in API)
  _t?: number; // Cache-busting parameter
};

/**
 * Unified filters that can be applied across all log types
 */
export interface UnifiedLogFilters {
  // Common filters
  level?: LogLevel[];
  source?: string[];
  category?: string[];
  userId?: string[];
  ipAddress?: string[];
  traceId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  tags?: string[];

  // Specialized filters
  errorFilters?: {
    resolved?: boolean;
    hasStackTrace?: boolean;
    endpoint?: string[];
  };

  userActionFilters?: {
    action?: string[];
    resourceType?: string[];
    sessionId?: string[];
  };

  databaseFilters?: {
    queryType?: string[];
    minExecutionTime?: number;
    maxExecutionTime?: number;
    databaseName?: string[];
    tableName?: string[];
  };

  apiFilters?: {
    method?: string[];
    endpoint?: string[];
    statusCode?: number[];
    minResponseTime?: number;
    maxResponseTime?: number;
  };

  securityFilters?: {
    eventType?: SecurityEventType[];
    severity?: SecuritySeverity[];
    minThreatLevel?: number;
    maxThreatLevel?: number;
    alertTriggered?: boolean;
  };

  // Advanced filtering
  correlationFilters?: {
    correlateByTraceId?: boolean;
    correlateByUserId?: boolean;
    correlateByIpAddress?: boolean;
    timeWindow?: number; // minutes
  };

  performanceFilters?: {
    slowQueriesOnly?: boolean;
    slowApiCallsOnly?: boolean;
    errorRateThreshold?: number;
  };

  anomalyFilters?: {
    detectAnomalies?: boolean;
    anomalyThreshold?: number;
  };
}

// ============================================================================
// ANALYTICS AND STATISTICS INTERFACES
// ============================================================================

/**
 * Log volume metrics across different dimensions
 */
export interface LogVolumeMetrics {
  totalLogs: number;
  logsByType: Record<LogType, number>;
  logsByLevel: Record<LogLevel, number>;
  logsPerHour: TimeSeriesData[];
  logsPerDay: TimeSeriesData[];
  growthRate: number;
  peakHours: string[];
}

/**
 * Time series data point
 */
export interface TimeSeriesData {
  timestamp: string;
  value: number;
  label?: string;
}

/**
 * System health status based on log analysis
 */
export interface SystemHealthStatus {
  overallScore: number; // 0-100
  errorRate: number;
  performanceScore: number;
  securityScore: number;
  availabilityScore: number;
  lastAssessment: Date;
  recommendations: string[];
  criticalIssues: string[];
  warnings: string[];
}

/**
 * Cross-log analytics for correlation analysis
 */
export interface CrossLogAnalytics {
  totalLogVolume: LogVolumeMetrics;
  errorRateAnalysis: ErrorRateMetrics;
  userActivityPatterns: UserActivityMetrics;
  systemPerformanceMetrics: PerformanceMetrics;
  securityThreatLevel: SecurityMetrics;
  correlatedEvents: CorrelatedEventAnalysis[];
}

/**
 * Error rate metrics
 */
export interface ErrorRateMetrics {
  currentErrorRate: number;
  previousErrorRate: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  errorsBySource: Record<string, number>;
  errorsByEndpoint: Record<string, number>;
  criticalErrors: number;
  resolvedErrors: number;
  unresolvedErrors: number;
}

/**
 * User activity metrics
 */
export interface UserActivityMetrics {
  activeUsers: number;
  totalSessions: number;
  averageSessionDuration: number;
  topActions: Array<{ action: string; count: number }>;
  usersByLocation: Record<string, number>;
  peakActivityHours: string[];
}

/**
 * Performance metrics
 */
export interface PerformanceMetrics {
  averageApiResponseTime: number;
  averageDatabaseQueryTime: number;
  slowestEndpoints: Array<{ endpoint: string; averageTime: number }>;
  slowestQueries: Array<{ query: string; averageTime: number }>;
  throughput: number;
  bottlenecks: string[];
}

/**
 * Security metrics
 */
export interface SecurityMetrics {
  threatLevel: number; // 0-100
  securityEvents: number;
  criticalSecurityEvents: number;
  failedAuthAttempts: number;
  suspiciousActivities: number;
  topThreats: Array<{ type: string; count: number; severity: SecuritySeverity }>;
  riskScore: number;
}

/**
 * Correlated event analysis
 */
export interface CorrelatedEventAnalysis {
  id: string;
  eventPattern: string;
  relatedLogs: Array<{
    logType: LogType;
    logId: string;
    timestamp: string;
    correlation: number; // 0-1
  }>;
  impact: 'low' | 'medium' | 'high' | 'critical';
  recommendation: string;
}

// ============================================================================
// BULK OPERATIONS INTERFACES
// ============================================================================

/**
 * Bulk operation types
 */
export type BulkOperationType =
  | 'resolve'
  | 'unresolve'
  | 'delete'
  | 'archive'
  | 'export'
  | 'tag'
  | 'untag';

/**
 * Bulk operation request
 */
export interface BulkOperationRequest {
  operation: BulkOperationType;
  logTypes: LogType[];
  logIds?: string[];
  filters?: UnifiedLogFilters;
  options?: Record<string, any>;
}

/**
 * Bulk operation result
 */
export interface BulkOperationResult {
  success: boolean;
  processedCount: number;
  failedCount: number;
  errors: string[];
  operationId: string;
  completedAt: string;
  duration: number; // milliseconds
}

// ============================================================================
// EXPORT INTERFACES
// ============================================================================

/**
 * Export formats supported
 */
export type ExportFormat = 'csv' | 'json' | 'xlsx' | 'pdf';

/**
 * Export configuration
 */
export interface ExportConfiguration {
  format: ExportFormat;
  logTypes: LogType[];
  filters?: UnifiedLogFilters;
  includeMetadata: boolean;
  includeAnalytics: boolean;
  compressionEnabled: boolean;
  encryptionEnabled: boolean;
  maxRecords: number;
  customFields?: string[];
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Export result
 */
export interface ExportResult {
  success: boolean;
  downloadUrl?: string;
  fileName: string;
  fileSize: number;
  recordCount: number;
  exportId: string;
  expiresAt: string;
  error?: string;
}

// ============================================================================
// REAL-TIME MONITORING INTERFACES
// ============================================================================

/**
 * Alert types
 */
export type AlertType =
  | 'error_threshold'
  | 'security_threat'
  | 'performance_degradation'
  | 'system_failure'
  | 'anomaly_detected'
  | 'custom';

/**
 * Alert configuration
 */
export interface AlertConfiguration {
  id: string;
  name: string;
  type: AlertType;
  logTypes: LogType[];
  conditions: AlertCondition[];
  actions: AlertAction[];
  enabled: boolean;
  cooldownPeriod: number; // minutes
}

/**
 * Alert condition
 */
export interface AlertCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'not_contains';
  value: any;
  timeWindow?: number; // minutes
}

/**
 * Alert action
 */
export interface AlertAction {
  type: 'email' | 'webhook' | 'notification' | 'escalation';
  configuration: Record<string, any>;
}

/**
 * Active alert
 */
export interface ActiveAlert {
  id: string;
  configurationId: string;
  type: AlertType;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  triggeredAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  resolvedAt?: string;
  relatedLogs: string[];
  metadata: Record<string, any>;
}

// ============================================================================
// TESTING AND VALIDATION INTERFACES
// ============================================================================

/**
 * Test log configuration
 */
export interface TestLogConfig {
  logType: LogType;
  level: LogLevel;
  count: number;
  message?: string;
  source?: string;
  category?: string;
  includeStackTrace?: boolean;
  simulateError?: boolean;
  customFields?: Record<string, any>;
}

/**
 * Validation result
 */
export interface ValidationResult {
  success: boolean;
  message: string;
  details?: Record<string, any>;
  timestamp: string;
  duration: number; // milliseconds
}

/**
 * Load test configuration
 */
export interface LoadTestConfig {
  logType: LogType;
  logsPerSecond: number;
  duration: number; // seconds
  concurrency: number;
  includeQueries: boolean;
}

/**
 * Load test result
 */
export interface LoadTestResult {
  success: boolean;
  totalLogs: number;
  averageLatency: number;
  maxLatency: number;
  minLatency: number;
  throughput: number;
  errors: number;
  duration: number; // milliseconds
}

// ============================================================================
// PAGINATION AND RESPONSE INTERFACES
// ============================================================================

/**
 * Paginated response for log queries
 */
export interface PaginatedLogResponse<T> {
  data: T[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Log statistics response
 */
export interface LogStatistics {
  volumeMetrics: LogVolumeMetrics;
  systemHealth: SystemHealthStatus;
  crossLogAnalytics: CrossLogAnalytics;
  generatedAt: string;
  timeRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Log trend data
 */
export interface LogTrendData {
  timeRange: {
    startDate: string;
    endDate: string;
  };
  trends: Array<{
    logType: LogType;
    data: TimeSeriesData[];
    trend: 'increasing' | 'decreasing' | 'stable';
    changePercentage: number;
  }>;
  generatedAt: string;
}